// (unchanged imports)
import { ToolbarGroup } from "./toolbarGroup.mjs";
import { BaseComponent } from "../../baseComponent.mjs";
import { Div } from "../../elements/div.mjs";
import { iconComponentFor } from "../../elements/ph-icon.mjs";
import { Button } from "../../elements/buttons.mjs";
import van from "../../../vanjs.mjs";

import {VisibilityManager} from "../../mixins/visibilityManager.mjs";

const { div, span, i } = van.tags;

/**
 * @class Toolbar
 * @extends BaseComponent
 * @description Floating/Draggable toolbar that auto-switches orientation
 * and tells children (groups/separators) how to align via a `toolbar:measure`
 * event fired on the internal [data-toolbar-root] wrapper.
 *
 * @param {object}  options
 * @param {boolean} [options.horizontalOnly=false] Lock to horizontal mode.
 * @param {number}  [options.edgeThreshold=96] Distance in px to a screen edge
 *                                             where the bar flips vertical.
 */
class Toolbar extends BaseComponent {
    // Monotonic counter used to cascade-stagger the default position of
    // freshly-created toolbars that have no persisted AppCache position.
    // Without this every new toolbar lands on the same default coordinates.
    static _spawnIndex = 0;

    constructor(options = undefined, ...args) {
        options = super(options, ...args).options;
        args = this._children;

        this.tabs = {};

        this.header = new Div({
            id: this.id + "-header",
            extraClasses: { tabs: "tabs", style: "tabs-boxed", events: "pointer-events-auto" }
        });

        // Plain container, not a `.join`: it holds one content Div per tab, and
        // as a join its first/last-child radius rules would reach through those
        // wrappers and flatten every button group inside into a single pill.
        // No `boxed2` either — that class carries its own padding + margin, which
        // is the padding the toolbar was carrying around its groups for nothing.
        this.body = new ToolbarGroup(
            {
                id: this.id + "-body",
                join: false,
                extraClasses: {
                    height: "h-full",
                    width: "w-full",
                    events: "pointer-events-auto"
                }
            },
        );

        // group to replace div
        // new Div({
        //     id: this.id + "-body",
        //     extraClasses: {
        //         height: "h-full",
        //         width: "w-full",
        //         style: "boxed2",
        //         margin: "m-0",
        //         events: "pointer-events-auto"
        //     }
        // });

        if (APPLICATION_CONTEXT.AppCache.get(`${this.id}-body-visible`, true)) {
            this.body.setClass("display", "");
        } else {
            this.body.setClass("display", "display-none");
        }

        // state
        this.display = APPLICATION_CONTEXT.AppCache.get(`${this.id}-visible`, true) ? "" : "none";
        this._dir = "horizontal";
        this._edgeThreshold = Number.isFinite(options.edgeThreshold) ? Number(options.edgeThreshold) : 96;
        this._horizontalOnly = !!options.horizontalOnly;
        this._mobileBreakpoint = APPLICATION_CONTEXT.getOption("maxMobileWidthPx", undefined, false, true);

        const wMin = this.options.horizontalOnly ? "min-w-max" : "";
        this.classMap["base"] = `draggable flex flex-col bg-transparent pointer-events-none ${wMin} ${this.options.pluginRootClass || ""}`;

        // buffer children into tabs
        for (let i of args) this.addToToolbar(i);
        this._children = [];

        // refs
        this._outerEl = null;
        this._rootWrap = null;
        this._observer = null;
        this._lastBox = null;
        this._fmToken = null;
        this._dragEnabled = false;

        this._embeddedMode = false;
        this._managedVisible = true;
        this._embeddedTitle = options.embeddedTitle || null;
        this._embeddedIcon = options.embeddedIcon || null;
        // Opt-in: on first run (no persisted `${id}-embed-pref`) this toolbar
        // docks into the app bar. MainLayout reads this in `registerToolbar`.
        this._defaultEmbedded = !!options.defaultEmbedded;

        // Whether the toolbar *wants* to live in the app bar. This is the
        // runtime source of truth (MainLayout routes on it); AppCache is only
        // best-effort persistence, so embedding still works when the cache is
        // bypassed (e.g. demo sessions with `bypassCache: true`). Seed from the
        // persisted value if present, else from the `defaultEmbedded` opt-in.
        const persistedPref = APPLICATION_CONTEXT.AppCache.get(`${this.id}-embed-pref`, null);
        this._embedPreference = persistedPref === true || persistedPref === "true" ? true
            : persistedPref === false || persistedPref === "false" ? false
                : this._defaultEmbedded;

        this.visibility = new VisibilityManager(this);
        USER_INTERFACE.AppBar.View.registerViewComponent('toolbarMenu',
            {
                id: this.id,
                icon: this._embeddedIcon || undefined,
                title: this._embeddedTitle || this.id,
                visibilityManager: this.visibility
            }
        );
    }

    addToToolbar(item) {
        if (!(item.id && item.icon && item.title)) {
            throw new Error("Item for menu needs every property set.");
        }
        const tab = this._createTab(item);
        this.tabs[item.id] = tab;
        tab.headerButton.attachTo(this.header);
        if (tab.contentDiv) tab.contentDiv.attachTo(this.body);

        // hide header if there's only one tab
        if (Object.keys(this.tabs).length === 1) {
            this.focus(item.id);
            this.header.setClass("display", "hidden");
        } else {
            this.header.setClass("display", "");
        }
        this.display = "";
    }

    _createTab(item) {
        const rawBody = item.body;
        const inText  = item.title;
        const inIcon  = item.icon instanceof BaseComponent
            ? item.icon
            : iconComponentFor(item.icon);

        const action = item.onClick || (() => {});

        const b = new Button({
            id: this.id + "-b-" + item.id,
            base: "tab",
            type: Button.TYPE.NONE,
            extraProperties: { title: inText },
            onClick: () => {
                action();
                this.focus(item.id);
            }
        }, inIcon, span(inText));

        // --- normalize body ---
        let content = [];
        if (rawBody != null) {
            if (Array.isArray(rawBody)) content = rawBody.slice();
            else content = [rawBody];
        }

        // Only wrap if there are multiple *components* (keep old HTML/string usage working)
        const allComponents = content.every(c => c instanceof BaseComponent);
        if (allComponents && content.length > 1) {
            // `join: false`: this wrapper only holds the real groups and the
            // separators between them. As a `.join` it would swallow them all
            // into one pill (DaisyUI's first/last-child radius rules reach
            // through wrappers), destroying the per-group grouping.
            const rootGroup = new ToolbarGroup(
                { id: `${this.id}-rootgroup-${item.id}`, join: false },
                ...content
            );
            content = [rootGroup];
        }

        let c;
        if (content.length) {
            c = new Div({
                id: this.id + "-c-" + item.id,
                extraClasses: { display: "display-none", height: "h-full" }
            }, ...content);
        }

        return { headerButton: b, contentDiv: c };
    }

    create() {
        const pos = this._resolveFloatingPosition(null, null);
        const left = pos.left;
        const top  = pos.top;

        this._outerEl = div(
            {
                ...this.commonProperties,
                class: ((this.commonProperties?.class ?? "") + " flex items-center").trim(),
                style: `
        position: fixed;
        left: ${left}px;
        top: ${top}px;
        display: ${this.display};
            `,
                ...this.extraProperties,
            },
            // MODIFIED: Removed 'width-full' and 'flex-grow' to prevent it from taking up space in horizontal mode
            div({ class: "spacer flex place-content-center" },

                // --- Hide Button (Commented Out) ---
                /*
                div({
                    class: "toolbar-hide badge badge-soft badge-secondary pointer-events-auto self-center text-xs mb-1",
                    style: "width: min(45px, 90%);",
                    onclick: () => this._toggle_body()
                }, i({ class: "ph-light ph-eye-slash" })),
                */

                // --- Handle (Simplified) ---
                // Removed fixed width styles and large badge classes for a cleaner look
                div({
                        // DaisyUI-themed drag handle (visible across themes)
                        class: "handle toolbar-handle-slim pointer-events-auto self-center rounded-md bg-base-200/80 text-base-content border border-base-300 shadow cursor-grab active:cursor-grabbing hover:bg-base-300/80",
                        style: "touch-action: none;"
                    },
                    i({ class: "ph-light ph-dots-six text-base-content" })
                ),

                // Dock + close stacked vertically next to the drag handle so the
                // two small icons take one column's width instead of two. Dock
                // embeds into the app bar (floating mode only — hidden when
                // embedded; un-dock via the host-bar float button); close hides
                // the toolbar (re-openable from the AppBar → View dropdown).
                div({ class: "flex flex-col gap-0.5 self-center ml-1" },
                    div({
                            class: "toolbar-dock pointer-events-auto px-1.5 py-0.5 rounded-md bg-base-200/80 text-base-content border border-base-300 shadow cursor-pointer hover:bg-primary/20 hover:text-primary text-xs leading-none",
                            title: $.t("toolbar.dock"),
                            onclick: () => this._requestEmbed(true),
                        },
                        i({ class: "ph-light ph-push-pin" })
                    ),
                    div({
                            class: "toolbar-close pointer-events-auto px-1.5 py-0.5 rounded-md bg-base-200/80 text-base-content border border-base-300 shadow cursor-pointer hover:bg-error/20 hover:text-error text-xs leading-none",
                            title: $.t("toolbar.hide"),
                            onclick: () => this._requestClose(),
                        },
                        i({ class: "ph-light ph-x" })
                    )
                )
            ),
            // No padding: the frosted box hugs the button groups. Padding here
            // only inflated the toolbar's footprint — the groups already carry
            // their own rounding and the separators the spacing.
            div({ "data-toolbar-root": "", class: "pointer-events-auto glass p-0 rounded-md" }, this.body.create())
        );

        this._rootWrap = this._outerEl.querySelector("[data-toolbar-root]");

        // Wire the VisibilityManager so its on/off route through
        // `setManagedVisible` — that keeps the VM's `_visible` field and
        // the toolbar's own `_managedVisible` state (the single source of
        // truth used by `_applyManagedVisibility`, MainLayout's per-host
        // toolbar layout, etc.) in lockstep. `params.ui.toolBar = false`
        // boots every toolbar hidden; user can re-open from the AppBar
        // toolbar menu (vm.set(true) → setManagedVisible(true) → display).
        const initialToolbarVisible = APPLICATION_CONTEXT?.getUiOption?.("toolBar");
        this.visibility.init(
            () => this.setManagedVisible(true),
            () => this.setManagedVisible(false),
            initialToolbarVisible === false ? false : undefined
        );

        queueMicrotask(() => {
            if (this._embeddedMode) {
                this._applyEmbeddedStyles();
                this._setOrientation("horizontal", true);
            } else {
                this._applyFloatingStyles();
                // Restore the last resolved orientation rather than recomputing
                // it from the freshly-created (unlaid-out) rect: the position →
                // orientation calc depends on the toolbar's own width, which
                // depends on the orientation, so an early recompute can flip a
                // mid-screen toolbar to vertical until the first drag. A drag
                // re-resolves and re-persists it (see checkRect).
                const cachedDir = APPLICATION_CONTEXT.AppCache.get(`${this.id}-Orientation`, null);
                const canUseCache = (cachedDir === "vertical" || cachedDir === "horizontal")
                    && !this._horizontalOnly
                    && !(this._mobileBreakpoint && window.innerWidth < this._mobileBreakpoint);
                if (canUseCache) {
                    this._setOrientation(cachedDir, true);
                } else {
                    this._updateOrientationFromPosition(true);
                }
                this._ensureFloatingRegistration();
            }
            this._ensureDragBinding();
            this._setupObservers();
            this._applyManagedVisibility();
        });

        return this._outerEl;
    }

    _setOrientation(dir, force = false) {
        if (!force && this._dir === dir) return;
        this._dir = dir;

        const wrap = this._rootWrap;
        if (!wrap) return;

        // Reset inner wrapper classes
        wrap.classList.remove("w-10", "h-10", "w-full", "h-full", "flex", "flex-row", "flex-col", "items-center");
        wrap.classList.add("flex", "items-center");

        // --- NEW LOGIC: Manipulate _outerEl to switch handle position ---
        // Horizontal Mode: outerEl = Row (Handle Left), wrap = Row (Items horizontal)
        // Vertical Mode:   outerEl = Col (Handle Top),  wrap = Col (Items vertical)

        if (this._outerEl) {
            this._outerEl.classList.remove("flex-row", "flex-col", "flex-col-reverse");
            if (dir === "horizontal") {
                this._outerEl.classList.add("flex-row");
            } else {
                // If the toolbar is touching the top of the screen, put the handle *below*
                // the toolbar to avoid a weird gap above the first row of tools.
                const top = this._outerEl.getBoundingClientRect().top;
                if (top <= 1) {
                    this._outerEl.classList.add("flex-col-reverse");
                } else {
                    this._outerEl.classList.add("flex-col");
                }
            }
        }

        if (dir === "horizontal") {
            wrap.classList.add("flex-row", "w-full");
        } else {
            wrap.classList.add("flex-col", "h-full");
        }

        wrap.dispatchEvent(new CustomEvent("toolbar:measure", {
            bubbles: true,
            detail: { dir: this._dir, size: 32, gap: "gap-1" }
        }));

        // (Optional) Logic for hiding buttons is technically unused now since they are commented out,
        // but safe to keep in case you uncomment them later.
        const header = this._outerEl?.querySelector(".toolbar-header") || this._outerEl;
        if (header) {
            const sideButtons = header.querySelectorAll(".toolbar-hide");
            sideButtons.forEach(btn => {
                if (dir === "horizontal") {
                    btn.classList.remove("hidden");
                } else {
                    btn.classList.add("hidden");
                }
            });
        }
    }


    getRootNode() {
        return this._outerEl || document.getElementById(this.id) || null;
    }

    getDefaultEmbedded() {
        return this._defaultEmbedded;
    }

    /** Runtime "wants to be embedded" state (source of truth for routing). */
    getEmbedPreference() {
        return this._embedPreference;
    }

    /** Set the embed preference; persists best-effort (no-op when cache bypassed). */
    setEmbedPreference(value) {
        this._embedPreference = !!value;
        APPLICATION_CONTEXT.AppCache.set(`${this.id}-embed-pref`, value ? "true" : "false");
    }

    /** Ask the layout to dock this toolbar into the app bar (or pop it out). */
    _requestEmbed(embedded) {
        window.LAYOUT?.setToolbarEmbedded?.(this.id, !!embedded);
    }

    /**
     * Drag-to-dock hit-test: when a floating toolbar is dropped overlapping the
     * app-bar slot, request embedding. Returns true when it triggered embedding.
     */
    _maybeDockFromDrag() {
        if (this._embeddedMode || !this._outerEl) return false;
        const slotRect = USER_INTERFACE?.AppBar?.ToolbarSlot?.getRect?.();
        if (!slotRect || slotRect.width <= 0) return false;
        const r = this._outerEl.getBoundingClientRect();
        const intersects = r.left < slotRect.right && r.right > slotRect.left
            && r.top < slotRect.bottom && r.bottom > slotRect.top;
        if (!intersects) return false;
        this._requestEmbed(true);
        return true;
    }

    getEmbeddedMeta() {
        const firstTab = Object.keys(this.tabs)[0];
        const firstHeader = firstTab ? this.tabs[firstTab]?.headerButton : null;
        return {
            id: this.id,
            title: this._embeddedTitle || firstHeader?.extraProperties?.title?.val || this.id,
            icon: this._embeddedIcon || 'ph-wrench'
        };
    }

    isRequestedVisible() {
        return typeof this.visibility?.is === "function"
            ? !!this.visibility.is()
            : this.display !== "none";
    }

    setManagedVisible(visible) {
        this._managedVisible = !!visible;
        this._applyManagedVisibility();
    }

    setEmbeddedMode(embedded) {
        this._embeddedMode = !!embedded;
        if (this._embeddedMode) {
            this._disableFloatingRegistration();
            this._applyEmbeddedStyles();
            this._setOrientation("horizontal", true);
        } else {
            this._applyFloatingStyles();
            this._updateOrientationFromPosition(true);
            this._ensureFloatingRegistration();
        }
        this._applyManagedVisibility();
    }

    _applyManagedVisibility() {
        const root = this.getRootNode();
        if (!root) return;
        root.style.display = this._managedVisible ? "" : "none";
    }

    /**
     * Width of the toolbar's content box when laid out horizontally, in px —
     * what it would occupy inside a host bar (the drag handle / dock / close
     * chrome is hidden when embedded and is deliberately not counted).
     *
     * Returns 0 while the toolbar is laid out vertically: a column's width says
     * nothing about its horizontal footprint, and guessing would be worse than
     * letting the caller fall back to its own cached measurement.
     *
     * @return {number}
     */
    getHorizontalContentWidth() {
        const wrap = this._rootWrap;
        if (!wrap || wrap.classList.contains("flex-col")) return 0;
        return Math.ceil(wrap.scrollWidth || wrap.getBoundingClientRect().width || 0);
    }

    _applyEmbeddedStyles() {
        const root = this.getRootNode();
        const wrap = this._rootWrap;
        if (!root || !wrap) return;

        root.style.position = "relative";
        root.style.left = "";
        root.style.top = "";
        root.style.zIndex = "";
        root.style.maxWidth = "100%";
        root.style.width = "auto";
        root.style.transform = "";

        root.classList.remove("flex-col", "flex-col-reverse");
        root.classList.add("flex-row", "items-center", "max-w-full", "w-auto");

        const spacer = root.querySelector(".spacer");
        spacer?.classList.add("hidden");
        root.querySelector(".handle")?.classList.add("hidden");
        root.querySelector(".toolbar-close")?.classList.add("hidden");
        root.querySelector(".toolbar-dock")?.classList.add("hidden");

        wrap.classList.remove("w-10", "h-10", "w-full", "h-full", "flex-col", "flex-col-reverse");
        wrap.classList.add("flex", "flex-row", "items-center", "max-w-full");
        // Drop the toolbar's own frosted box when embedded — the host bar (and
        // the app bar itself) already provides the chrome; doubling it makes the
        // toolbar taller than the 35px bar and adds a nested border.
        wrap.classList.remove("glass", "p-0", "rounded-md");
    }

    // Resolve where this toolbar should be placed when entering floating mode.
    // Priority: existing inline position (authoritative runtime state — keeps
    // drag-in-progress and post-snap positions stable across re-applies) →
    // persisted AppCache position → per-instance cascading default that
    // staggers fresh toolbars so they don't pile on top of each other.
    _resolveFloatingPosition(currentLeftStr, currentTopStr) {
        let left = null;
        let top  = null;

        if (typeof currentLeftStr === "string" && currentLeftStr !== "") {
            const cur = parseFloat(currentLeftStr);
            if (Number.isFinite(cur)) left = cur;
        }
        if (typeof currentTopStr === "string" && currentTopStr !== "") {
            const cur = parseFloat(currentTopStr);
            if (Number.isFinite(cur)) top = cur;
        }

        if (left === null) {
            const rawL = APPLICATION_CONTEXT.AppCache.get(`${this.id}-PositionLeft`);
            const cachedL = (rawL !== null && rawL !== undefined && rawL !== "") ? Number(rawL) : null;
            if (Number.isFinite(cachedL)) left = cachedL;
        }
        if (top === null) {
            const rawT = APPLICATION_CONTEXT.AppCache.get(`${this.id}-PositionTop`);
            const cachedT = (rawT !== null && rawT !== undefined && rawT !== "") ? Number(rawT) : null;
            if (Number.isFinite(cachedT)) top = cachedT;
        }

        if (left === null || top === null) {
            // Allocate a per-instance cascade slot exactly once, so embedded ↔
            // floating toggles that clear inline left/top recover the same
            // default position without bumping the global spawn counter again
            // and without writing to AppCache for an un-dragged toolbar.
            if (!this._cascadePos) {
                const idx = Toolbar._spawnIndex++;
                const v = 50 + idx * 28;
                this._cascadePos = { left: v, top: v };
            }
            if (left === null) left = this._cascadePos.left;
            if (top === null) top = this._cascadePos.top;
        }

        return { left, top };
    }

    _applyFloatingStyles() {
        const root = this.getRootNode();
        const wrap = this._rootWrap;
        if (!root || !wrap) return;

        root.style.position = "fixed";
        const pos = this._resolveFloatingPosition(root.style.left, root.style.top);
        root.style.left = `${pos.left}px`;
        root.style.top = `${pos.top}px`;
        root.style.maxWidth = "";
        root.style.width = "";

        const spacer = root.querySelector(".spacer");
        spacer?.classList.remove("hidden");
        root.querySelector(".handle")?.classList.remove("hidden");
        root.querySelector(".toolbar-close")?.classList.remove("hidden");
        root.querySelector(".toolbar-dock")?.classList.remove("hidden");

        wrap.classList.remove("max-w-full");
        // Restore the floating toolbar's own frosted box.
        wrap.classList.add("glass", "p-0", "rounded-md");
    }

    _ensureFloatingRegistration() {
        if (this._embeddedMode || !this._outerEl) return;
        const clamp = {
            margin: 6,
            topBarId: "top-side",
            cache: {
                leftKey: `${this.id}-PositionLeft`,
                topKey:  `${this.id}-PositionTop`
            }
        };
        if (this._fmToken) {
            UI.Services.FloatingManager.updateClamp?.(this._fmToken, clamp);
        } else {
            this._fmToken = UI.Services.FloatingManager.register({
                el: this._outerEl,
                owner: this,
                clamp
            });
        }
        this._ensureDragBinding();
    }

    _disableFloatingRegistration() {
        if (!this._fmToken) return;
        UI.Services.FloatingManager.updateClamp?.(this._fmToken, null);
    }

    _ensureDragBinding() {
        if (this._dragEnabled || !this._outerEl || !this._fmToken) return;
        const handle = this._outerEl.querySelector(".handle");
        if (!handle) return;
        this._dragEnabled = true;
        UI.Services.FloatingManager.enableDrag(this._fmToken, {
            handle,
            persist: {
                leftKey: `${this.id}-PositionLeft`,
                topKey:  `${this.id}-PositionTop`
            },
            onMove: () => {
                this._updateOrientationFromPosition(false, { dragging: true });
                if (this._rootWrap) {
                    this._rootWrap.dispatchEvent(new CustomEvent("toolbar:measure", {
                        bubbles: true,
                        detail: { dir: this._dir, size: 32, gap: "gap-1" }
                    }));
                }
            }
        });
    }

    
    isVisible() { return !this._outerEl.classList.contains("display-none"); }

    async _requestClose() {
        const ok = await UI.Modal.confirm({
            header: $.t("toolbar.hide"),
            body: $.t("toolbar.confirmHide"),
            confirmLabel: $.t("toolbar.hide"),
        });
        if (ok) this.visibility.set(false);
    }
    
    // _toggle_body() {
    //     if (this.body.classMap.display === "display-none") {
    //         this.body.setClass("display", "");
    //         APPLICATION_CONTEXT.AppCache.get(`${this.id}-body-visible`, "true");
    //     } else {
    //         this.body.setClass("display", "display-none");
    //         APPLICATION_CONTEXT.AppCache.set(`${this.id}-body-visible`, "false");
    //     }
    // }

    // ----- observers & orientation -----
    _setupObservers() {
        const root = this._outerEl;
        const wrap = this._rootWrap;
        if (!root || !wrap) return;

        const notifyMeasure = () => {
            wrap.dispatchEvent(new CustomEvent("toolbar:measure", {
                bubbles: true,
                detail: { dir: this._dir, size: 32, gap: "gap-1" }
            }));
        };

        const seedLastBox = () => {
            const r = root.getBoundingClientRect();
            this._lastBox = `${Math.round(r.left)}:${Math.round(r.top)}`;
        };

        const onViewportChange = () => {
            // Window resize / scroll re-snaps the toolbar to the nearest edge
            // but MUST NOT persist that position — otherwise the side effect
            // of any layout-shift (including another toolbar opening and
            // triggering FloatingManager's _clampAll) overwrites every
            // already-visible toolbar's saved coordinates.
            this._updateOrientationFromPosition(false, { persist: false });
            notifyMeasure();
            // Reset the rect baseline so the next mouseup doesn't mistake a
            // resize-induced move for a user drag and persist it.
            seedLastBox();
        };

        const checkRect = () => {
            const r = root.getBoundingClientRect();
            const key = `${Math.round(r.left)}:${Math.round(r.top)}`;
            if (this._lastBox !== key) {
                this._lastBox = key;
                // Drag-to-dock: if the user dropped the toolbar over the app-bar
                // slot, embed it. The floating position has already been
                // persisted during the drag, so it restores on un-dock. Bail
                // before the floating-persist branch below.
                if (this._maybeDockFromDrag()) return;
                // Canonical end-of-interaction persist. The snap branch in
                // _updateOrientationFromPosition only writes when the toolbar
                // lands near an edge; mid-screen drops would otherwise depend
                // solely on FM's per-pointermove writes, which can be lost
                // when the pointermove stream is throttled or interrupted by
                // a scroll/resize. Writing here gives every user-initiated
                // rect change exactly one guaranteed AppCache write.
                APPLICATION_CONTEXT.AppCache.set(`${this.id}-PositionLeft`, Math.round(r.left));
                APPLICATION_CONTEXT.AppCache.set(`${this.id}-PositionTop`,  Math.round(r.top));
                this._updateOrientationFromPosition(false, { persist: true });
                // Persist the resolved orientation so the next startup restores
                // it directly instead of recomputing from an unstable rect.
                APPLICATION_CONTEXT.AppCache.set(`${this.id}-Orientation`, this._dir);
                notifyMeasure();
            }
        };

        this._observer?.disconnect?.();
        this._observer = new ResizeObserver(() => notifyMeasure());
        this._observer.observe(wrap);

        window.addEventListener("resize", onViewportChange);
        window.addEventListener("scroll", onViewportChange, { passive: true });

        root.addEventListener("mouseup", checkRect);
        root.addEventListener("touchend", checkRect);
        root.querySelector(".handle")?.addEventListener("pointerup", () => setTimeout(checkRect, 0));

        // Seed the baseline so a click on a tool button (mouseup with the
        // toolbar at its already-correct position) is not interpreted as a
        // user move.
        seedLastBox();
        notifyMeasure();
    }

    _updateOrientationFromPosition(force = false, opts = {}) {
        if (!this._outerEl || !this._rootWrap) return;

        if (this._embeddedMode) {
            this._applyEmbeddedStyles();
            this._setOrientation("horizontal", true);
            return;
        }

        const rect = this._outerEl.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const snapDist = this._edgeThreshold;
        const dragging = !!opts.dragging;

        // When dragging near corners, width/height changes after an orientation flip can cause
        // edge-distance calculations to oscillate. Capture a stable "drag size" for the whole drag.
        if (dragging) {
            if (!this._dragSize) {
                this._dragSize = { w: rect.width, h: rect.height, max: Math.max(rect.width, rect.height) };
            }
        } else {
            this._dragSize = null;
        }
        // Add a little hysteresis to prevent rapid flip-flopping when the toolbar size changes
        // between horizontal/vertical near an edge (common in corners).
        const enterEdge = this._edgeThreshold;
        const leaveEdge = this._edgeThreshold + 24;

        // current position
        let left = rect.left;
        let top  = rect.top;
        let snapped = false;

        // distances to edges
        const distL = rect.left;
        const distR = vw - (rect.left + rect.width);
        const distT = rect.top;
        const distB = vh - (rect.top + rect.height);

        // The edge-snap branch only runs on user-initiated calls
        // (post-drag mouseup / pointerup paths, signalled by
        // `opts.persist === true`). Init, window-resize, scroll, and
        // setEmbeddedMode/onLayoutChange paths must NOT yank the toolbar
        // to a corner — that's how staggered cascade defaults survive and
        // how an already-visible toolbar keeps its position when another
        // toolbar is opened.
        if (!dragging && opts.persist === true) {
            // --- snap to left/right ---
            if (distL <= snapDist) {
                left = 0;
                snapped = true;
            } else if (distR <= snapDist) {
                left = vw - rect.width;
                snapped = true;
            }

            // --- snap to top/bottom ---
            if (distT <= snapDist) {
                top = 0;
                snapped = true;
            } else if (distB <= snapDist) {
                top = vh - rect.height;
                snapped = true;
            }

            if (snapped) {
                const l = Math.round(left);
                const t = Math.round(top);
                this._outerEl.style.left = `${l}px`;
                this._outerEl.style.top  = `${t}px`;

                APPLICATION_CONTEXT.AppCache.set(`${this.id}-PositionLeft`, l);
                APPLICATION_CONTEXT.AppCache.set(`${this.id}-PositionTop`,  t);

                // let FloatingManager re-clamp if needed (top bar, margins, etc.)
                if (this._fmToken && UI.Services.FloatingManager.clampNow) {
                    UI.Services.FloatingManager.clampNow(this._fmToken);
                }
            }

        }

        // --- orientation: vertical near a side edge, else horizontal ---
        if (this._horizontalOnly || window.innerWidth < this._mobileBreakpoint) {
            this._setOrientation("horizontal", force);
            return;
        }

        const curLeft = parseFloat(this._outerEl.style.left) || rect.left;
        const distL2 = curLeft;
        const effW = (dragging && this._dragSize) ? this._dragSize.max : rect.width;
        const distR2 = vw - (curLeft + effW);

        // Hysteresis:
        // - If we're currently horizontal, only enter vertical when we're clearly near a side edge.
        // - If we're currently vertical, only return to horizontal once we've moved away from the edge.
        const nearSideEnter = (distL2 <= enterEdge) || (distR2 <= enterEdge);
        const awayFromSideLeave = (distL2 > leaveEdge) && (distR2 > leaveEdge);

        let nextDir;
        if (this._dir === "vertical") {
            nextDir = awayFromSideLeave ? "horizontal" : "vertical";
        } else {
            nextDir = nearSideEnter ? "vertical" : "horizontal";
        }

        this._setOrientation(nextDir, force);
    }

    focus(id) {
        if (id in this.tabs) {
            this.unfocusAll();
            this.tabs[id].headerButton.setClass("tab-active", "tab-active");
            this.tabs[id].contentDiv?.setClass("display", "");
            this._focused = id;
            return true;
        }
        return false;
    }
    unfocusAll() {
        for (let tab of Object.values(this.tabs)) {
            tab.headerButton.setClass("tab-active", "");
            tab.contentDiv?.setClass("display", "display-none");
        }
        this._focused = undefined;
    }

    onLayoutChange(details) {
        const root = this._outerEl;
        if (!root) return;

        const width = details?.width ?? window.innerWidth;
        const isCompact = this._embeddedMode || width < this._mobileBreakpoint;

        if (isCompact) {
            this.setClass("mobile", "mobile");
            root.querySelector(".handle")?.classList.add("hidden");
            root.querySelector(".toolbar-close")?.classList.add("hidden");
            root.querySelector(".toolbar-dock")?.classList.add("hidden");
            this._applyEmbeddedStyles();
            this._setOrientation("horizontal", true);
        } else {
            this.setClass("mobile", "");
            root.querySelector(".handle")?.classList.remove("hidden");
            root.querySelector(".toolbar-close")?.classList.remove("hidden");
            root.querySelector(".toolbar-dock")?.classList.remove("hidden");
            this._applyFloatingStyles();
            this._ensureFloatingRegistration();
            this._updateOrientationFromPosition(true);
        }

        this._applyManagedVisibility();
    }
}

export { Toolbar };